import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  BookOpen,
  FolderKanban,
  Radio,
  Activity,
  Settings,
  Building2,
  ScrollText,
  HardDrive,
  MapPin,
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  FileCheck,
  Handshake,
  Mail,
  Map,
  Network,
  Presentation,
  ShieldCheck,
  Wrench,
  ClipboardList,
  Link,
  Users,
  BarChart3,
  LibraryBig,
  Home,
  FileLock2,
  Gauge,
} from "lucide-react";
import type { TranslationKey } from "@/lib/i18n";

/**
 * What a menu entry promises (#379).
 *
 * - `live`    — verified surface, the default when the field is absent.
 * - `beta`    — usable but unfinished; offered with a visible Beta marker.
 * - `planned` — the route resolves but the screen behind it is not built yet
 *               (#460). Distinct from `beta`: beta promises something that
 *               works imperfectly, planned promises nothing yet. Stays on the
 *               menu — an entry that says so is more use than a missing one —
 *               and carries its own marker.
 * - `legacy`  — superseded by Frontier. The route stays mounted so deep links
 *               and bookmarks keep working, but the entry leaves the menu
 *               unless the build sets `VITE_SHOW_LEGACY_NAV=true`.
 */
export type NavItemStatus = "live" | "beta" | "planned" | "legacy";

export type NavItemGroup = "stages" | "tools";

export interface NavItem {
  name: string;
  labelKey?: TranslationKey;
  href: string;
  icon: LucideIcon;
  children?: NavItem[];
  matchPrefix?: string;
  /** If set, only these security roles can see this item. */
  requiredRole?: string[];
  /** If set, the user must have this effective permission. */
  requiredPermission?: string;
  /** If set, shown only while this session feature flag is true (missing = off). */
  requiredFeatureFlag?: string;
  /** Delivery status of the surface behind this entry. Defaults to `live`. */
  status?: NavItemStatus;
  /** The heading a project menu entry sits under; none for the summary. */
  group?: NavItemGroup;
  /**
   * Free-form tags the operator can attach via the WebMap editor.
   * Pure annotation/classification — not used for routing or filtering.
   * Persisted through the localStorage override.
   */
  tags?: string[];
}

export interface NavSection {
  items: NavItem[];
}

/** Security roles that define nav visibility categories. */
export type SecurityRole = "platform_admin" | "org_admin" | "distributor_admin";

/**
 * Filter nav items based on the user's security role and effective permissions.
 * Items without requiredRole/requiredPermission are visible to everyone with shell access.
 */
export function filterNavByRole(
  items: NavItem[],
  securityRole: string,
  effectivePermissions: string[] = [],
): NavItem[] {
  return items
    .filter((item) => {
      if (item.requiredRole && !item.requiredRole.includes(securityRole)) {
        return false;
      }
      if (
        item.requiredPermission &&
        !effectivePermissions.includes(item.requiredPermission)
      ) {
        return false;
      }
      return true;
    })
    .map((item) => {
      if (!item.children) return item;
      const filteredChildren = filterNavByRole(
        item.children,
        securityRole,
        effectivePermissions,
      );
      return { ...item, children: filteredChildren };
    });
}

// ---------------------------------------------------------------------------
// Command status table (#379)
//
// One source of truth for what each Command surface is. The static nav below
// declares the same status inline (a contract test keeps the two in step), and
// `useShellNav` re-asserts it over any persisted menu-config payload, so a
// stored override written before this field existed cannot silently put a
// legacy page back on the menu.
// ---------------------------------------------------------------------------

/** Superseded by Frontier — mounted for deep links, off the menu. */
const COMMAND_LEGACY_PREFIXES = [
  "/admin/dashboard",
  "/admin/projects",
  "/admin/simulator",
  "/admin/inventory",
  "/admin/field-ops",
  "/admin/operations",
  "/admin/garage",
  "/admin/provisioning",
  "/admin/commissioning",
  "/admin/twins",
  "/admin/web-map",
];

/** Usable but unfinished — offered with a Beta marker. */
const COMMAND_BETA_PREFIXES = [
  "/admin/evaluation",
  // #466 — read half only. The chain, holds and manifests are real and
  // verified; purge/hold/export writes are deliberately absent, so the entry
  // promises something that works imperfectly rather than something whole.
  "/admin/evidence",
  "/admin/partnerships",
  "/admin/email-templates",
  "/admin/runtime",
  "/admin/distributor/consumption-report",
];

/** Route resolves, screen not built yet — offered with a Planned marker. */
const COMMAND_PLANNED_PREFIXES: string[] = [];

function matchesRoutePrefix(href: string, prefix: string): boolean {
  return href === prefix || href.startsWith(`${prefix}/`);
}

/**
 * Canonical status for a Command href, or `undefined` when the surface is a
 * governed one (i.e. live). Prefix-scoped so sub-routes inherit the verdict of
 * their section, and boundary-aware so `/admin/projects-governance` is not
 * mistaken for `/admin/projects`.
 */
export function commandNavStatus(href: string): NavItemStatus | undefined {
  if (
    COMMAND_BETA_PREFIXES.some((prefix) => matchesRoutePrefix(href, prefix))
  ) {
    return "beta";
  }
  if (
    COMMAND_PLANNED_PREFIXES.some((prefix) => matchesRoutePrefix(href, prefix))
  ) {
    return "planned";
  }
  if (
    COMMAND_LEGACY_PREFIXES.some((prefix) => matchesRoutePrefix(href, prefix))
  ) {
    return "legacy";
  }
  return undefined;
}

/** What the heritage notice on a legacy Command page says and links to. */
export interface CommandLegacyNotice {
  /** Per-session dismissal key for the banner. */
  moduleName: string;
  /** Frontier equivalent — omitted when none exists, so no dead-end link. */
  opsRoute?: string;
  /** Extra guidance, e.g. which project tab replaced the page. */
  hintKey?: TranslationKey;
}

const COMMAND_LEGACY_NOTICES: Array<CommandLegacyNotice & { prefix: string }> =
  [
    {
      prefix: "/admin/projects",
      moduleName: "legacy-projects",
      opsRoute: "/ops/projects",
    },
    {
      prefix: "/admin/simulator",
      moduleName: "legacy-simulator",
      opsRoute: "/ops/projects",
    },
    {
      prefix: "/admin/provisioning",
      moduleName: "provisioning",
      opsRoute: "/ops/projects",
      hintKey: "ops.migrationBanner.hint.provisioning",
    },
    {
      prefix: "/admin/commissioning",
      moduleName: "commissioning",
      opsRoute: "/ops/projects",
      hintKey: "ops.migrationBanner.hint.commissioning",
    },
    { prefix: "/admin/twins", moduleName: "twins", opsRoute: "/ops/projects" },
    { prefix: "/admin/dashboard", moduleName: "legacy-dashboard" },
    { prefix: "/admin/inventory", moduleName: "legacy-inventory" },
    { prefix: "/admin/field-ops", moduleName: "legacy-field-ops" },
    { prefix: "/admin/operations", moduleName: "legacy-operations" },
    { prefix: "/admin/garage", moduleName: "legacy-garage" },
    { prefix: "/admin/web-map", moduleName: "legacy-web-map" },
  ];

/**
 * The heritage notice a Command path must show, or `null` for a governed page.
 *
 * Driven by the same status table as the menu, so a page cannot be hidden from
 * the menu without telling whoever deep-links into it why it looks abandoned.
 */
export function commandLegacyNotice(
  pathname: string,
): CommandLegacyNotice | null {
  if (commandNavStatus(pathname) !== "legacy") return null;
  const match = COMMAND_LEGACY_NOTICES.find((notice) =>
    matchesRoutePrefix(pathname, notice.prefix),
  );
  if (!match) return null;
  return {
    moduleName: match.moduleName,
    opsRoute: match.opsRoute,
    hintKey: match.hintKey,
  };
}

/** True when this build was made with `VITE_SHOW_LEGACY_NAV=true`. */
export function legacyNavVisible(): boolean {
  return import.meta.env.VITE_SHOW_LEGACY_NAV === "true";
}

/**
 * Drop legacy entries from a nav tree. Read at call time (not module load) so
 * the flag can be exercised in tests and so the sidebar re-resolves normally.
 */
export function filterNavByStatus(
  items: NavItem[],
  showLegacy: boolean = legacyNavVisible(),
): NavItem[] {
  return items
    .filter((item) => showLegacy || item.status !== "legacy")
    .map((item) => {
      if (!item.children) return item;
      const children = filterNavByStatus(item.children, showLegacy);
      return { ...item, children: children.length > 0 ? children : undefined };
    });
}
// ---------------------------------------------------------------------------
// Command menu — governance only (#379)
//
// Order: identity & access, then the governed catalogue, then the commercial
// surfaces, then platform oversight, then the unfinished (beta) surfaces.
// Everything Frontier now owns lives at the bottom marked `legacy`: still in
// the list so `VITE_SHOW_LEGACY_NAV=true` can reveal it for internal work,
// filtered out of the rendered menu otherwise.
// ---------------------------------------------------------------------------
export const adminMainNav: NavItem[] = [
  // === HOME ===
  {
    // #402 — the posture home. No requiredRole: the active-context card reads
    // the session and answers for every role, and the cards behind a platform
    // authority are gated inside the page rather than by hiding the entry.
    name: "Home",
    labelKey: "nav.home",
    href: "/admin/home",
    icon: Home,
  },

  // === IDENTITY & ACCESS ===
  {
    name: "Organizations",
    labelKey: "nav.organizations",
    href: "/admin/organizations",
    icon: Building2,
  },
  {
    // Platform-admin only: /accounts/profiles/ and the membership/scope-grant
    // writes behind the detail page are platform governance surfaces, and an
    // item that only ever produced a denial is what #375 removed.
    name: "Users",
    labelKey: "nav.users",
    href: "/admin/users",
    icon: Users,
    requiredRole: ["platform_admin"],
  },
  {
    name: "Workspaces",
    labelKey: "nav.workspaces",
    href: "/admin/workspaces",
    icon: Network,
    requiredRole: ["platform_admin"],
  },
  {
    name: "Roles",
    labelKey: "nav.roles",
    href: "/admin/roles",
    icon: ShieldCheck,
    requiredRole: ["platform_admin"],
  },
  {
    name: "Project access",
    labelKey: "nav.projectAccess",
    href: "/admin/project-access",
    icon: Users,
    requiredPermission: "command.project_access.read",
  },

  // === CATALOG ===
  {
    name: "Catalog",
    labelKey: "nav.catalog",
    href: "/admin/catalog",
    icon: BookOpen,
    matchPrefix: "/admin/catalog",
    requiredRole: ["platform_admin"],
  },

  // === DISTRIBUTOR ===
  {
    name: "Distributor",
    labelKey: "nav.distributor",
    href: "/admin/distributor",
    icon: Building2,
    matchPrefix: "/admin/distributor",
    requiredRole: ["distributor_admin", "platform_admin"],
    children: [
      {
        name: "Dashboard",
        labelKey: "nav.dashboard",
        href: "/admin/distributor",
        icon: LayoutDashboard,
      },
      {
        name: "Pool Status",
        labelKey: "nav.poolStatus",
        href: "/admin/distributor/pool-status",
        icon: Package,
      },
      {
        // Client creation is an action on the clients page, not a menu entry.
        name: "Clients",
        labelKey: "nav.clients",
        href: "/admin/distributor/clients",
        icon: Users,
      },
      {
        name: "Portfolio Publications",
        labelKey: "nav.portfolioPublications",
        href: "/admin/distributor/portfolio",
        icon: LibraryBig,
        // The page drives `command.portfolio` (PortfolioCommandPermission), a
        // Command-authority surface. Platform admins hold folded global-object
        // authority; a distributor_admin without the capability would only
        // reach a denial, so the item stays platform-admin only — same shape
        // as the Consumption Report sibling below.
        requiredRole: ["platform_admin"],
      },
      {
        name: "Consumption Report",
        labelKey: "nav.consumptionReport",
        href: "/admin/distributor/consumption-report",
        icon: BarChart3,
        requiredRole: ["platform_admin"],
        status: "beta",
      },
    ],
  },

  // === COMMERCIAL GOVERNANCE ===
  {
    name: "Pricing governance",
    labelKey: "nav.pricing",
    href: "/admin/pricing",
    icon: BarChart3,
    requiredPermission: "command.price_books.read",
  },
  {
    name: "Documentary governance",
    labelKey: "nav.documentaryRequirements",
    href: "/admin/documentary-requirements",
    icon: FileCheck,
    requiredPermission: "command.documentary_profiles.read",
  },
  {
    name: "Presentation",
    labelKey: "nav.presentation",
    href: "/admin/presentation",
    icon: Presentation,
    requiredRole: ["platform_admin"],
  },

  // === PLATFORM OVERSIGHT ===
  {
    name: "Connector registry",
    labelKey: "nav.integrations",
    href: "/admin/integrations",
    icon: Link,
    requiredPermission: "command.connector_registry.read",
  },
  {
    // /admin/audit reads /api/v1/audit/global/, which is IsHumanPlatformAdmin
    // gated. Offering it to org_admin only ever produced a 403 page.
    name: "Audit",
    labelKey: "nav.audit",
    href: "/admin/audit",
    icon: ScrollText,
    requiredRole: ["platform_admin"],
  },
  {
    name: "Quality & Evaluation",
    labelKey: "nav.qualityEvaluation",
    href: "/admin/evaluation",
    icon: Gauge,
    requiredRole: ["platform_admin"],
    status: "beta",
  },

  // === BETA — usable, still being finished ===
  {
    // Read-only evidence (#466). `evidence.governance.read` is exactly what
    // /api/v1/evidence/legal-holds/ and /export-manifests/ enforce — the UI
    // does not invent a looser tier than the API already answers to.
    name: "Evidence",
    labelKey: "nav.evidence",
    href: "/admin/evidence",
    icon: FileLock2,
    requiredPermission: "evidence.governance.read",
    status: "beta",
  },
  {
    name: "Partnerships",
    labelKey: "nav.partnerships",
    href: "/admin/partnerships",
    icon: Handshake,
    requiredRole: ["platform_admin"],
    status: "beta",
  },
  {
    name: "Email templates",
    labelKey: "nav.emailTemplates",
    href: "/admin/email-templates",
    icon: Mail,
    requiredRole: ["platform_admin"],
    status: "beta",
  },
  {
    name: "Runtime",
    labelKey: "nav.runtime",
    href: "/admin/runtime",
    icon: Activity,
    requiredRole: ["platform_admin"],
    status: "beta",
  },

  // === LEGACY — Frontier owns these; hidden unless VITE_SHOW_LEGACY_NAV ===
  {
    name: "Dashboard",
    labelKey: "nav.dashboard",
    href: "/admin/dashboard",
    icon: LayoutDashboard,
    status: "legacy",
  },
  {
    name: "Projects",
    labelKey: "nav.projects",
    href: "/admin/projects",
    icon: FolderKanban,
    matchPrefix: "/admin/projects|/admin/simulator",
    requiredRole: ["platform_admin"],
    status: "legacy",
    children: [
      {
        name: "Projects",
        labelKey: "nav.projects",
        href: "/admin/projects",
        icon: FolderKanban,
        status: "legacy",
      },
      {
        name: "Simulator",
        labelKey: "nav.simulator",
        href: "/admin/simulator",
        icon: Radio,
        status: "legacy",
      },
    ],
  },
  {
    name: "Inventory",
    labelKey: "nav.inventory",
    href: "/admin/inventory/locations",
    icon: Package,
    matchPrefix: "/admin/inventory",
    requiredRole: ["platform_admin"],
    status: "legacy",
    children: [
      {
        name: "Warehouses",
        labelKey: "nav.warehouses",
        href: "/admin/inventory/locations",
        icon: MapPin,
        status: "legacy",
      },
      {
        name: "Stock",
        labelKey: "nav.stock",
        href: "/admin/inventory/stock",
        icon: Package,
        status: "legacy",
      },
      {
        name: "Assets",
        labelKey: "nav.assets",
        href: "/admin/inventory/assets",
        icon: HardDrive,
        status: "legacy",
      },
      {
        name: "Receipts",
        labelKey: "nav.receipts",
        href: "/admin/inventory/receipts",
        icon: ArrowDownToLine,
        status: "legacy",
      },
      {
        name: "Shipments",
        labelKey: "nav.shipments",
        href: "/admin/inventory/shipments",
        icon: ArrowUpFromLine,
        status: "legacy",
      },
      {
        name: "Movements",
        labelKey: "nav.movements",
        href: "/admin/inventory/movements",
        icon: ArrowLeftRight,
        status: "legacy",
      },
    ],
  },
  {
    name: "Field Operations",
    labelKey: "nav.fieldOperations",
    href: "/admin/field-ops/work-orders",
    icon: Wrench,
    matchPrefix: "/admin/field-ops",
    status: "legacy",
    children: [
      {
        name: "Work Orders",
        labelKey: "nav.workOrders",
        href: "/admin/field-ops/work-orders",
        icon: ClipboardList,
        status: "legacy",
      },
      {
        name: "Commissioning",
        labelKey: "nav.commissioning",
        href: "/admin/field-ops/commissioning",
        icon: FileCheck,
        status: "legacy",
      },
      {
        name: "Pairing",
        labelKey: "nav.pairing",
        href: "/admin/field-ops/pairing",
        icon: Link,
        status: "legacy",
      },
    ],
  },
  {
    name: "Admin Tools",
    labelKey: "nav.adminTools",
    href: "/admin/web-map",
    icon: Wrench,
    matchPrefix: "/admin/web-map",
    requiredRole: ["platform_admin"],
    status: "legacy",
    children: [
      {
        name: "Web Map (Temp)",
        labelKey: "nav.webMapTemp",
        href: "/admin/web-map",
        icon: Map,
        status: "legacy",
      },
    ],
  },
];

export const adminBottomNav: NavItem[] = [
  {
    name: "Settings",
    labelKey: "nav.settings",
    href: "/admin/settings",
    icon: Settings,
    requiredRole: ["platform_admin"],
  },
];
